import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  Truck, CurrencyDollar, PaperPlaneTilt, EnvelopeSimple, ArrowRight,
  SignOut, FilePdf, Package, Wallet, TrendUp, CheckCircle, Clock,
  Storefront, Sparkle, MapPin, Camera, CircleNotch,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "fai_driver_token";

// Module-scoped cache of in-flight verify promises. Deduplicates parallel
// verify calls for the same token (React StrictMode dev double-invocation,
// browser prefetch, etc). Magic links are single-use — never fire twice.
const _verifyPromises = new Map();

async function _dedupedVerify(token) {
  const existing = _verifyPromises.get(token);
  if (existing) return existing;
  const p = axios.post(`${API}/driver-app/verify`, { token }).finally(() => {
    setTimeout(() => _verifyPromises.delete(token), 5000);
  });
  _verifyPromises.set(token, p);
  return p;
}

const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

function driverClient() {
  const token = localStorage.getItem(TOKEN_KEY);
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

// ---------- Login screen ----------
function DriverLogin() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/driver-app/request-magic-link`, {
        email: email.trim(),
      });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429) {
        toast.error("Too many requests. Try again in a bit.");
      } else {
        toast.error(detail || "Could not send link. Check your email.");
      }
    } finally { setSending(false); }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6" data-testid="driver-login-sent">
        <div className="w-full max-w-md text-center">
          <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#FF5A1F]/[0.15] text-[#FF5A1F]">
            <EnvelopeSimple size={32} weight="fill" />
          </div>
          <h1 className="font-display text-3xl font-semibold text-white mb-2">Check your email</h1>
          <p className="text-neutral-400 text-sm mb-6">
            We sent a sign-in link to <b className="text-white">{email}</b>.
            The link expires in 20 minutes.
          </p>
          <button
            onClick={() => { setSent(false); setEmail(""); }}
            data-testid="driver-login-try-again"
            className="text-sm text-neutral-500 hover:text-white underline"
          >
            Use a different email
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6" data-testid="driver-login-page">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FF5A1F]/[0.15] text-[#FF5A1F] mb-4">
            <Truck size={28} weight="fill" />
          </div>
          <h1 className="font-display text-3xl font-semibold text-white mb-1">My Pay Portal</h1>
          <p className="text-sm text-neutral-500">Sign in to view your pay, loads, and paystubs.</p>
        </div>

        <form onSubmit={submit} className="card-surface p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-2">
              Your work email
            </label>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="driver-login-email"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-3 text-white text-base"
            />
          </div>
          <button
            type="submit"
            disabled={sending || !email.trim()}
            data-testid="driver-login-submit"
            className="w-full btn-primary rounded-full py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <PaperPlaneTilt size={14} weight="fill" />
            {sending ? "Sending link..." : "Send me a sign-in link"}
          </button>
          <div className="text-xs text-neutral-500 text-center">
            No password needed. We'll email you a one-time link.
          </div>
        </form>

        <div className="mt-6 text-center text-xs text-neutral-600">
          Ask your dispatcher to add your email to your driver profile if you don't receive a link.
        </div>
      </div>
    </div>
  );
}

// ---------- Verify screen (handles ?token= query param) ----------
function DriverVerify({ onSuccess }) {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("verifying");
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setErrorMsg("No token in URL");
      return;
    }
    (async () => {
      try {
        const { data } = await _dedupedVerify(token);
        localStorage.setItem(TOKEN_KEY, data.token);
        onSuccess?.(data);
        setStatus("ok");
        setTimeout(() => navigate("/pay", { replace: true }), 400);
      } catch (err) {
        setStatus("error");
        setErrorMsg(err?.response?.data?.detail || "Invalid or expired link.");
      }
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6" data-testid="driver-verify-page">
      <div className="text-center max-w-md">
        {status === "verifying" && (
          <>
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FF5A1F]/[0.15] text-[#FF5A1F] mb-4 animate-pulse">
              <Clock size={28} />
            </div>
            <div className="text-white font-medium text-lg">Signing you in…</div>
          </>
        )}
        {status === "ok" && (
          <>
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/20 text-emerald-400 mb-4">
              <CheckCircle size={28} weight="fill" />
            </div>
            <div className="text-white font-medium text-lg">Signed in! Loading…</div>
          </>
        )}
        {status === "error" && (
          <>
            <div className="text-red-400 font-medium mb-2" data-testid="driver-verify-error">{errorMsg}</div>
            <a href="/pay" className="text-sm text-[#FF5A1F] hover:underline">Request a new link →</a>
          </>
        )}
      </div>
    </div>
  );
}

// ---------- Dashboard ----------
function StatCard({ label, value, sub, accent = "text-white", icon: Icon, testid }) {
  return (
    <div className="card-surface p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-neutral-500">
        {Icon && <Icon size={12} weight="fill" />}
        {label}
      </div>
      <div className={`text-3xl font-display font-semibold mt-1 tabular-nums ${accent}`}>{value}</div>
      {sub && <div className="text-[11px] text-neutral-600 mt-1">{sub}</div>}
    </div>
  );
}

// ---------- Fleet Top Picks (read-only, from owner's saved lanes) ----------
const _pickTierTone = {
  strong: { text: "text-emerald-400", bg: "bg-emerald-500/12", border: "border-emerald-400/30", label: "STRONG" },
  good:   { text: "text-[#FF5A1F]",   bg: "bg-[#FF5A1F]/12",   border: "border-[#FF5A1F]/30",   label: "GOOD" },
};
const _fmtAgeShort = (mins) => {
  if (!mins && mins !== 0) return "";
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  return h < 24 ? `${h}h` : `${Math.floor(h / 24)}d`;
};

function DriverTopPicksCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    driverClient()
      .get("/driver-app/top-picks?limit=3")
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData({ picks: [], saved_lanes_count: 0 }))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  // Hide entirely when the fleet has no saved lanes (no signal to surface)
  if (loading) return null;
  if (!data || data.saved_lanes_count === 0) return null;

  const picks = data.picks || [];

  return (
    <div className="card-surface p-5 sm:p-6" data-testid="driver-top-picks-card">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF5A1F] mb-1">
            <Sparkle size={12} weight="fill" />
            LOADS YOUR FLEET IS WATCHING
          </div>
          <h3 className="font-display text-lg sm:text-xl font-semibold text-white leading-tight">
            {picks.length > 0
              ? `${picks.length} top match${picks.length === 1 ? "" : "es"} from dispatch's saved lanes`
              : "No fresh picks right now"}
          </h3>
        </div>
      </div>

      {picks.length === 0 ? (
        <div className="p-4 border border-dashed border-white/10 rounded-lg text-center text-xs text-neutral-500" data-testid="driver-top-picks-empty">
          Scanned {data.saved_lanes_count} saved lane{data.saved_lanes_count === 1 ? "" : "s"} — no STRONG matches right now.
        </div>
      ) : (
        <div className="space-y-2" data-testid="driver-top-picks-list">
          {picks.map((p) => <DriverPickRow key={p.match_id} pick={p} />)}
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-white/5 text-[10px] text-neutral-500 leading-relaxed">
        These loads match your dispatcher's saved lanes on the FreightPilot Load Board.
        Talk to dispatch if you're available to take one.
      </div>
    </div>
  );
}

function DriverPickRow({ pick }) {
  const tone = _pickTierTone[pick.ai_tier] || _pickTierTone.good;
  return (
    <div
      className={`p-3 rounded-md bg-white/[0.015] border ${tone.border}`}
      data-testid={`driver-top-pick-${pick.match_id.slice(0, 8)}`}
    >
      <div className="flex items-center gap-3">
        {/* Score badge */}
        <div className={`shrink-0 w-14 rounded-md ${tone.bg} border ${tone.border} py-1.5 text-center`}>
          <div className={`font-display text-lg font-semibold leading-none ${tone.text}`}>
            {Math.round(pick.ai_score)}
          </div>
          <div className={`text-[8px] tracking-widest ${tone.text} mt-1`}>{tone.label}</div>
        </div>

        {/* Lane */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-white text-sm font-medium truncate">
            <MapPin size={12} className="text-neutral-500 shrink-0" />
            <span className="truncate">{pick.origin_city}, {pick.origin_state}</span>
            <ArrowRight size={11} className="text-neutral-600 mx-0.5 shrink-0" />
            <span className="truncate">{pick.destination_city}, {pick.destination_state}</span>
          </div>
          <div className="text-[11px] text-neutral-500 mt-0.5 truncate">
            {(pick.miles || 0).toLocaleString()}mi
            {pick.deadhead_miles > 0 && <> · {pick.deadhead_miles}mi DH</>}
            <span className="hidden sm:inline"> · {pick.broker_company}</span>
            {pick.age_min !== undefined && <> · {_fmtAgeShort(pick.age_min)} ago</>}
          </div>
        </div>

        {/* Money */}
        <div className="shrink-0 text-right">
          <div className="text-sm font-mono text-white tabular-nums">
            ${Math.round(pick.posted_rate || 0).toLocaleString()}
          </div>
          <div className="text-[10px] text-neutral-500 mt-0.5">
            {(pick.rate_per_mile || 0).toFixed(2)}/mi
          </div>
        </div>
      </div>
    </div>
  );
}


function DriverDashboard({ profile, onLogout }) {
  const [summary, setSummary] = useState(null);
  const [paystubs, setPaystubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const c = driverClient();
      const [s, p] = await Promise.all([
        c.get("/driver-app/summary"),
        c.get("/driver-app/paystubs"),
      ]);
      setSummary(s.data);
      setPaystubs(p.data?.paystubs || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        onLogout?.();
        return;
      }
      toast.error("Could not load your dashboard");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const downloadPaystub = async (paystubId, ps, pe) => {
    setDownloadingId(paystubId);
    try {
      const res = await driverClient().get(
        `/driver-app/paystubs/${paystubId}.pdf`,
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `paystub_${ps}_to_${pe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download paystub");
    } finally { setDownloadingId(null); }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    onLogout?.();
    toast.success("Signed out");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-neutral-500">Loading your dashboard…</div>
      </div>
    );
  }

  const ytd = summary?.ytd || {};
  const first = (profile?.driver?.name || "").split(" ")[0] || "there";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="driver-dashboard">
      {/* Header */}
      <div className="border-b border-white/5 bg-[#0F0F0F] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold">My Pay</div>
            <div className="text-sm text-white mt-0.5 font-medium">
              {profile?.company?.name || "Fleet"}
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="driver-logout"
            className="text-xs text-neutral-500 hover:text-white inline-flex items-center gap-1"
          >
            <SignOut size={14} /> Sign out
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Greeting */}
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white">
            Hi, {first}
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Here's your year-to-date pay and load history.
          </p>
        </div>

        {/* Snap POD one-tap tile — parking-lot capture, camera-first */}
        <SnapPodTile onUploaded={load} />

        {/* YTD Net Pay banner (big highlight) */}
        <div className="rounded-2xl bg-gradient-to-br from-[#FF5A1F] to-[#E04A16] p-6 sm:p-8" data-testid="driver-ytd-net">
          <div className="text-[10px] uppercase tracking-widest text-black/60 font-bold">YEAR-TO-DATE NET PAY</div>
          <div className="text-4xl sm:text-5xl font-display font-bold text-black tabular-nums mt-2">
            {money(ytd.net_pay)}
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-black/70">
            <div>
              <span className="text-black font-semibold">{money(ytd.gross_pay)}</span> gross
            </div>
            <div>
              <span className="text-black font-semibold">−{money(ytd.deductions)}</span> deductions
            </div>
            <div>
              <span className="text-black font-semibold">{ytd.paystub_count || 0}</span> paystubs
            </div>
          </div>
        </div>

        {/* Stat grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="YTD Miles" testid="driver-stat-miles"
                    value={Number(ytd.miles || 0).toLocaleString()}
                    icon={TrendUp} />
          <StatCard label="YTD Loads" testid="driver-stat-loads"
                    value={ytd.load_count || 0}
                    icon={Package} />
          <StatCard label="YTD Revenue" testid="driver-stat-revenue"
                    value={money(ytd.revenue)}
                    sub="Load rates you generated" />
          <StatCard label="Fuel Costs" testid="driver-stat-fuel"
                    value={money(ytd.fuel_cost)}
                    accent="text-orange-400"
                    sub={profile?.driver?.employment_type === "owner_op" ? "Paid by you" : "Paid by company"} />
        </div>

        {/* Fleet top picks — read-only view of dispatch's best matches */}
        <DriverTopPicksCard />

        {/* Paystubs list */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">
            Your Paystubs ({paystubs.length})
          </div>
          {paystubs.length === 0 ? (
            <div className="card-surface p-8 text-center text-sm text-neutral-500" data-testid="driver-paystubs-empty">
              No paystubs yet. Your dispatcher will send them each pay period.
            </div>
          ) : (
            <div className="card-surface divide-y divide-white/5" data-testid="driver-paystubs-list">
              {paystubs.map((p) => (
                <div key={p.id}
                     className="p-4 flex flex-wrap items-center justify-between gap-3"
                     data-testid={`driver-paystub-${p.id}`}>
                  <div>
                    <div className="text-sm text-white font-medium">
                      {p.period_start} → {p.period_end}
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5">
                      {p.load_count} load{p.load_count === 1 ? "" : "s"}
                      <span className="text-neutral-700 mx-2">·</span>
                      Gross {money(p.gross_pay)}
                      <span className="text-neutral-700 mx-2">·</span>
                      <span className="text-red-400">−{money(p.deductions_total)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-lg font-display font-semibold text-emerald-400 tabular-nums">
                      {money(p.net_pay)}
                    </div>
                    <button
                      onClick={() => downloadPaystub(p.id, p.period_start, p.period_end)}
                      disabled={downloadingId === p.id}
                      data-testid={`driver-paystub-download-${p.id}`}
                      className="text-xs rounded-full px-3 py-1.5 border border-white/10 text-neutral-300 hover:text-white hover:border-white/25 inline-flex items-center gap-1 transition-colors disabled:opacity-60"
                    >
                      <FilePdf size={12} weight="fill" />
                      {downloadingId === p.id ? "..." : "PDF"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent loads */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">
            Recent Loads
          </div>
          {(summary?.recent_loads || []).length === 0 ? (
            <div className="card-surface p-8 text-center text-sm text-neutral-500" data-testid="driver-loads-empty">
              No loads yet.
            </div>
          ) : (
            <div className="card-surface divide-y divide-white/5" data-testid="driver-recent-loads">
              {summary.recent_loads.map((ld) => (
                <div key={ld.id} className="p-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-sm text-white font-medium">
                      {ld.origin} → {ld.destination}
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5">
                      Load {ld.load_number || "—"}
                      <span className="text-neutral-700 mx-2">·</span>
                      {ld.delivery_date || "—"}
                      <span className="text-neutral-700 mx-2">·</span>
                      {(ld.miles || 0).toLocaleString()} mi
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <PODUploadButton loadId={ld.id} loadNumber={ld.load_number} />
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${
                      ld.status === "delivered" ? "bg-emerald-500/20 text-emerald-400" :
                      ld.status === "in_transit" ? "bg-blue-500/20 text-blue-400" :
                      "bg-neutral-500/20 text-neutral-400"
                    }`}>
                      {(ld.status || "").replace("_", " ")}
                    </span>
                    <div className="text-sm text-white font-semibold tabular-nums">
                      {money(ld.rate)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-xs text-neutral-600 text-center py-4">
          Questions about your pay? Contact your dispatcher.
          {profile?.company?.contact_email && (
            <> <a href={`mailto:${profile.company.contact_email}`} className="text-[#FF5A1F] hover:underline">
              {profile.company.contact_email}
            </a></>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Main router ----------
export default function DriverPayPortal() {
  const [params] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const hasToken = () => !!localStorage.getItem(TOKEN_KEY);

  useEffect(() => {
    // If there's a ?token= in the URL, we're in verify mode — handled by DriverVerify below
    if (params.get("token")) { setLoading(false); return; }
    if (!hasToken()) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await driverClient().get("/driver-app/me");
        setProfile(data);
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
      } finally { setLoading(false); }
    })();
  }, [params]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-neutral-500">Loading…</div>
      </div>
    );
  }

  // Verify flow (magic link clicked)
  if (params.get("token")) {
    return <DriverVerify onSuccess={(data) => setProfile({
      driver: data.driver,
      company: data.company,
    })} />;
  }

  // Not signed in
  if (!profile) {
    return <DriverLogin />;
  }

  // Signed in
  return <DriverDashboard profile={profile} onLogout={() => setProfile(null)} />;
}


function PODUploadButton({ loadId, loadNumber }) {
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const inputRef = { current: null };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File too large (max 15 MB). Try a compressed photo.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "Proof of Delivery (POD)");
      fd.append("notes", "");
      await driverClient().post(
        `/driver-app/loads/${loadId}/upload-doc`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setUploaded(true);
      toast.success(`POD uploaded for ${loadNumber || "load"} — dispatch notified`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <input
        ref={(el) => { inputRef.current = el; }}
        type="file"
        accept="image/*,application/pdf"
        capture="environment"
        onChange={onPick}
        className="hidden"
        data-testid={`pod-upload-input-${loadId}`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        data-testid={`pod-upload-btn-${loadId}`}
        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-wider transition-colors ${
          uploaded
            ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
            : "bg-[#FF5A1F]/15 text-[#FF7038] border border-[#FF5A1F]/30 hover:bg-[#FF5A1F]/25"
        } disabled:opacity-60`}
      >
        {uploading ? (
          <><CircleNotch size={11} className="animate-spin" /> Uploading</>
        ) : uploaded ? (
          <><CheckCircle size={11} weight="fill" /> POD sent</>
        ) : (
          <><Camera size={11} weight="fill" /> Upload POD</>
        )}
      </button>
    </>
  );
}



// ---------- Snap POD one-tap tile (parking-lot capture) ----------
// Prominent home-screen tile on the driver dashboard. Auto-picks the
// driver's most recent non-delivered load and one-taps into the phone
// camera. Zero picker friction — the driver just delivered, opens the
// app, taps once, snaps the paperwork, done. Dispatch pings in real
// time via `core.pod_push_ping`.
//
// Load selection order:
//   1. Newest load with status in {arrived_delivery, in_transit,
//      arrived_pickup, assigned} — active delivery-flow loads first.
//   2. Fallback to the newest non-delivered load overall.
//   3. If none, tile stays disabled with a helpful message.
function SnapPodTile({ onUploaded }) {
  const [target, setTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const inputRef = { current: null };

  useEffect(() => {
    let alive = true;
    driverClient()
      .get("/driver-app/loads?limit=25")
      .then((r) => {
        if (!alive) return;
        const loads = r.data?.loads || [];
        // Prefer active delivery-flow loads first
        const priority = ["arrived_delivery", "in_transit", "arrived_pickup", "assigned"];
        const sorted = [...loads].sort((a, b) => {
          const ai = priority.indexOf(a.status);
          const bi = priority.indexOf(b.status);
          const aa = ai === -1 ? 99 : ai;
          const bb = bi === -1 ? 99 : bi;
          if (aa !== bb) return aa - bb;
          // Newest first within the same status bucket
          return (b.delivery_date || "").localeCompare(a.delivery_date || "");
        });
        // Skip delivered / cancelled — POD is upstream of those.
        const pick = sorted.find(
          (l) => l.status !== "delivered" && l.status !== "cancelled",
        );
        setTarget(pick || null);
      })
      .catch(() => alive && setTarget(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [uploadedCount]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file || !target) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File too large (max 15 MB). Try a compressed photo.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "Proof of Delivery (POD)");
      fd.append("notes", "");
      await driverClient().post(
        `/driver-app/loads/${target.id}/upload-doc`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(
        `POD sent to dispatch for ${target.load_number || "load"} ✓`,
      );
      setUploadedCount((n) => n + 1);
      onUploaded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  // While the driver's loads are loading, don't flash the tile — it
  // creates a weird disabled → enabled jump the moment the response
  // lands. Cheap to render nothing for ~150ms.
  if (loading) return null;

  const disabled = !target || uploading;
  const tileClasses = target
    ? "bg-gradient-to-br from-[#FF5A1F] to-[#E04A16] text-black cursor-pointer hover:shadow-lg hover:shadow-[#FF5A1F]/20"
    : "bg-[#141414] border border-white/10 text-neutral-500 cursor-not-allowed";

  return (
    <div data-testid="snap-pod-tile" className={`relative rounded-2xl p-5 sm:p-6 transition-all ${tileClasses}`}>
      <input
        ref={(el) => { inputRef.current = el; }}
        type="file"
        accept="image/*,application/pdf"
        capture="environment"
        onChange={onPick}
        className="hidden"
        data-testid="snap-pod-tile-input"
      />
      <button
        type="button"
        onClick={() => !disabled && inputRef.current?.click()}
        disabled={disabled}
        data-testid="snap-pod-tile-btn"
        className="w-full text-left disabled:cursor-not-allowed"
      >
        <div className="flex items-center gap-4">
          <div className={`shrink-0 h-14 w-14 sm:h-16 sm:w-16 rounded-full flex items-center justify-center ${
            target ? "bg-black/25" : "bg-white/[0.04]"
          }`}>
            {uploading ? (
              <CircleNotch size={28} className="animate-spin" />
            ) : (
              <Camera size={28} weight="fill" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className={`text-[10px] uppercase tracking-widest font-bold ${
              target ? "text-black/60" : "text-neutral-600"
            }`}>
              One-tap POD capture
            </div>
            <div className={`font-display text-2xl sm:text-3xl font-bold mt-0.5 leading-tight ${
              target ? "text-black" : "text-neutral-400"
            }`}>
              {uploading
                ? "Uploading…"
                : target
                ? "Snap POD"
                : "No active load"}
            </div>
            {target ? (
              <div className="text-xs sm:text-sm mt-1.5 text-black/70 line-clamp-1" data-testid="snap-pod-tile-target">
                For <b className="text-black">{target.load_number || `load ${target.id?.slice(0, 6)}`}</b>
                {target.destination && (
                  <> · {target.destination}</>
                )}
              </div>
            ) : (
              <div className="text-xs mt-1 text-neutral-600">
                No load in progress — ask dispatch to assign one.
              </div>
            )}
          </div>
          {target && !uploading && (
            <div className="hidden sm:flex shrink-0 items-center gap-1 rounded-full bg-black/25 px-3 py-1.5 text-[10px] uppercase tracking-widest text-black font-bold">
              Tap to snap →
            </div>
          )}
        </div>
      </button>
    </div>
  );
}
